import type { IAgentToolCall } from "./agentAnswer.types";

/** Client-side display rules for agent output: labels, plurals, failure copy. */

/**
 * Pluralizes an English noun.
 *
 * The service names entities in the singular ("bill", "jobsite"). One place to
 * pluralize, so callers never double up.
 */
export function pluralize(noun: string, count: number): string {
    if (count === 1) {
        return noun;
    }
    if (/(s|x|z|ch|sh)$/i.test(noun)) {
        return `${noun}es`;
    }
    if (/[^aeiou]y$/i.test(noun)) {
        return `${noun.slice(0, -1)}ies`;
    }
    return `${noun}s`;
}

/** Why a turn ended without an answer, as far as the client can tell. */
export type AgentFailure =
    /** The request never got a response: offline, or a proxy error with no CORS headers. */
    | { kind: "offline" }
    /** An HTTP status, from the response or from the stream's `error` event. */
    | { kind: "status"; status: number }
    /** The body ended with no `final`. */
    | { kind: "cut" }
    /** Nothing arrived for longer than the client waits. */
    | { kind: "timeout" };

/**
 * What the user reads when a turn fails. Never the server's text: that is
 * written for logs ("agent engine failed: DeadlineExceeded: …").
 */
export function describeAgentFailure(failure: AgentFailure): string {
    switch (failure.kind) {
        case "offline":
            return "Couldn't reach Ask. Check your connection.";
        case "cut":
            return "The answer stopped partway through.";
        case "timeout":
            return "Ask took too long to answer.";
        case "status":
            if (failure.status === 401 || failure.status === 403) {
                return "Ask couldn't confirm your sign-in. If this keeps happening, refresh the page.";
            }
            if (failure.status === 404) {
                return "Ask lost track of this conversation, so trying again starts a new one.";
            }
            if (failure.status === 429) {
                return "Ask is busy right now.";
            }
            return "Ask ran into a problem answering that.";
    }
}

/** Reads a string off the model's raw tool arguments, or null. */
function str(input: Record<string, unknown>, key: string): string | null {
    const raw = input[key];
    return typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : null;
}

/** An entity kind as words. The service mixes `_` and `-` separators. */
export function humanizeEntity(kind: string): string {
    return kind.replace(/[_-]+/g, " ");
}

/**
 * A tool call, humanized for the progress checklist.
 *
 * Reads the arguments defensively: the tool set belongs to the server, so a new
 * tool or a renamed argument must degrade to the bare tool name rather than
 * render "undefined" at the user.
 */
export function describeToolCall(call: IAgentToolCall): string {
    const { name, input } = call;
    const rawEntity = str(input, "entity_type");
    const entity = rawEntity === null ? undefined : humanizeEntity(rawEntity);

    if (name === "search") {
        const query = str(input, "query");
        return query ? `Searching for "${query}"` : "Searching";
    }

    if (name === "suggest") {
        const query = str(input, "query");
        return query ? `Checking spelling of "${query}"` : "Checking spelling";
    }

    if (name === "aggregate") {
        const metric = str(input, "metric") ?? "count";
        const verb = metric === "count" ? "Counting" : `Totaling ${metric} of`;
        return entity ? `${verb} ${entity}` : `${verb} records`;
    }

    if (name === "list_records") {
        return entity ? `Looking through ${pluralize(entity, 2)}` : "Looking through records";
    }

    if (name === "describe_records") {
        return entity ? `Checking what ${pluralize(entity, 2)} track` : "Checking the schema";
    }

    if (name === "resolve_record") {
        const target = str(input, "name");
        return target ? `Finding "${target}"` : "Finding a record";
    }

    if (name === "get_record") {
        const id = str(input, "id");
        return id ? `Reading ${id}` : "Reading a record";
    }

    return name;
}
